import { EditableCallbackParams, ValueGetterParams } from "ag-grid-community/dist/lib/entities/colDef";
import _ from "lodash";
import classnames from "classnames";
import { ColDef, ICellRendererParams, RowNode } from "ag-grid-community";
import { Schema } from "types/schema";
import { format } from "lib/globalize-util";
import { buildShipmentColDef, buildSizeColDef, getCellClasses } from "components/shipment-review/helpers";
import { IDetailsTotal, IRowData, ISize } from "components/shipment-review/types";
import { MORE_MENU_COL_WIDTH, OtherViewComponents, SizingViewComponents as Components } from "constants/sizingView";
import { Warehouse } from "@nuorder/client-cart-api/dist/types/modules/brand/models";
import { buildFdmColDef } from "components/shipment-review/helpers/buildFdmColDef";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { updateWorkingOrder, addLineItem, updateLineItem, updateSize, addSize } from "actions/cart-legacy";
import { postToWindow } from "helpers/window-messages";
import { getSupportedFDMFields, mapLineItem, mapSize } from "containers/legacy-cart/helpers/common";
import { isEqualBy } from "helpers/common";
import { getBulkUnits, isMatchLocation } from "helpers/legacy-cart/bulk";
import { AllocationType } from "types/cart";
import { SelectEditorProps } from "containers/legacy-cart/grid-components/SelectEditor";
import type { IBulkUnits, IDetailHeaderPrices, IOrderCurrency } from "types/cart/shipment-review";
import { InputRendererProps } from "containers/legacy-cart/grid-components/InputRenderer";
import { SHIPMENT_REVIEW } from "constants/integration/shipment-review";
import { BULK_UNITS_SIZE } from "constants/cart";
import { currencyValueFormatter } from "helpers/product-collection";
import { ICustomizationData } from "constants/product-customizations/customization";

const SHIPMENT_COL_WIDTH = 200;
const SIZE_COLUMN_WIDTH = 105;
const DROPDOWN_COLUMN_WIDTH = 150;

const getTotalColDef = (options: {
    headerName: string;
    colId: string;
    cellClass?: string;
    showStrikethroughedPrices?: boolean;
    getValue: (totals: IDetailsTotal) => any;
    valueFormatter?: ({ value }) => any;
    equals?: ColDef["equals"];
    width?: number;
}): ColDef => ({
    headerName: options.headerName,
    colId: options.colId,
    pinned: "right",
    width: options.width ?? SIZE_COLUMN_WIDTH,
    filter: false,
    headerClass: "right-aligned",
    cellClass: classnames(options.cellClass, `total-cell-${options.colId}`, "numeric", "right-aligned"),
    suppressMenu: true,
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    cellRendererSelector: (params: CastProp<ICellRendererParams, "data", IRowData>) => {
        if (params.node.rowPinned === "bottom") {
            return {
                component: Components.DetailsGridTotalsRowRenderer,
            };
        }
        if (options.showStrikethroughedPrices) {
            return { component: Components.PriceWithStrikethrough };
        }
    },
    equals: options.equals,
    valueGetter: ({ data }: { data: IRowData }) => (data?.totals ? options.getValue(data.totals) : ""),
    valueFormatter: options.valueFormatter,
});

const getDetailsMenuColDef = (uniqueSizes: ISize[]): ColDef => ({
    headerName: "",
    colId: "more-menu",
    pinned: "right",
    width: MORE_MENU_COL_WIDTH,
    cellClass: "details-menu-col",
    filter: false,
    resizable: false,
    suppressMenu: true,
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    suppressCellFlash: true,
    cellRendererSelector: (params: ICellRendererParams) => ({
        component: params.node.isRowPinned() ? Components.DetailsGridTotalsRowRenderer : Components.DetailsMenuRenderer,
        params: { uniqueSizes },
    }),
    valueGetter: ({ data }: CastProp<ValueGetterParams, "data", IRowData>) =>
        `${data.lineItemId}:${data.allocated}:${data.warehouse?.id}:${_.map(data.sizes, "quantity").join()}`, // unique key to update menu data on change (all used fields should be saved here to avoid caching)
});

const getWarehouseColDef = (): ColDef => ({
    headerName: format("Warehouse"),
    colId: "warehouse",
    width: DROPDOWN_COLUMN_WIDTH,
    suppressMenu: true,
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    singleClickEdit: true,
    cellEditorPopup: true,
    editable: ({ data, node }: { data: IRowData; node: RowNode }) =>
        _.size(data?.availableWarehouses) > 1 && !node.rowPinned && isMatchLocation(data),
    suppressKeyboardEvent: (params) => params.event.key === KEYBOARD_KEYS.ENTER && params.editing, // allow selection with Enter
    equals: isEqualBy<Warehouse & { _allocated: AllocationType }>(["id", "_allocated"]),
    valueGetter: (row: { data: IRowData }) => ({ _allocated: row.data.allocated, ...(row.data?.warehouse ?? null) }), // `_allocated` is a hack to force update styles when changing allocations rows while `cellClass` does not work
    valueFormatter: ({ value }: { value?: Warehouse }) => value?.name ?? "",
    cellRendererSelector: ({ node }) => ({
        component: node.rowPinned === "bottom" ? null : Components.SelectRenderer,
    }),
    cellEditorSelector: (params: { data: IRowData }) => ({
        component: Components.SelectEditor,
        params: {
            values: _.map(params.data?.availableWarehouses, (w) => ({ label: w.name, value: w.id, data: w })),
            formatValue: (w: Warehouse) => w?.id ?? "",
            confirmation: params.data.holdInventory
                ? {
                      title: format("Change Warehouse?"),
                      message: format(
                          "Changing warehouse will release inventory reservations and then attempt to reserve the same quantities. Are you sure you wish to change the warehouse?",
                      ),
                  }
                : null,
        } as SelectEditorProps<Warehouse>,
    }),
    valueSetter: (params: { oldValue: Warehouse; newValue: Warehouse; data: IRowData }) => {
        if (params.oldValue?.id === params.newValue?.id) {
            return false;
        }
        params.data.warehouse = params.newValue;
        return true;
    },
    onCellValueChanged: (params: { oldValue: Warehouse; newValue: Warehouse; data: IRowData }) => {
        if (params.oldValue?.id === params.newValue?.id) {
            return;
        }

        const isVirtual = !params.data.lineItemId;
        const isAllocatedByLocation = !!params.data.item.location?.id;
        const id = params.data.item.id;
        const liUpdate = { ...mapLineItem(params.data), warehouse: params.newValue?.id };

        if (!isAllocatedByLocation) {
            const action = isVirtual ? addLineItem(id, liUpdate) : updateLineItem(id, liUpdate);
            postToWindow(updateWorkingOrder(action));
            return;
        }

        const sizes = _.values(params.data.sizes);
        const actions = _.map(isVirtual ? _.take(sizes) : sizes, (size) => {
            const update = {
                ...mapSize(size.size),
                quantity: size.quantity,
                location: params.data.item.location?.id,
                warehouse: params.newValue?.id,
            };
            return isVirtual ? addSize(id, update, liUpdate) : updateSize(id, update);
        });
        postToWindow(updateWorkingOrder(actions));
    },
});

type IPrebook = { prebook: boolean; _allocated?: AllocationType; _canChange: boolean };
const getPrebookColDef = (): ColDef => ({
    headerName: format("Order Type"),
    colId: "prebook",
    width: DROPDOWN_COLUMN_WIDTH,
    suppressMenu: true,
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    singleClickEdit: true,
    cellEditorPopup: true,
    editable: ({ data, node }: { data: IRowData; node: RowNode }) =>
        !node.rowPinned && data.canChangePrebook && isMatchLocation(data),
    suppressKeyboardEvent: (params) => params.event.key === KEYBOARD_KEYS.ENTER && params.editing, // allow selection with Enter
    equals: isEqualBy<IPrebook>(["prebook", "_allocated", "_canChange"]),
    valueGetter: (row: { data: IRowData }): IPrebook => ({
        prebook: row.data?.prebook ?? false,
        _allocated: row.data.allocated, // `_allocated` is a hack to force update styles when changing allocations rows while `cellClass` does not work
        _canChange: row.data?.canChangePrebook, // `_canChange` is required to update styles when changing warehouse. Ex., prebook stays the same but now it is changeable
    }),
    valueFormatter: ({ value }: { value: IPrebook }) => (value.prebook ? format("Prebook") : format("Not Prebook")),
    cellRendererSelector: ({ node }) => ({
        component: node.rowPinned === "bottom" ? Components.DetailsGridTotalsRowRenderer : Components.SelectRenderer,
    }),
    cellEditorSelector: () => ({
        component: Components.SelectEditor,
        params: {
            values: [
                { label: format("Prebook"), value: true, data: { prebook: true } },
                { label: format("Not Prebook"), value: false, data: { prebook: false } },
            ],
            formatValue: (value: IPrebook) => value.prebook,
        } as SelectEditorProps<IPrebook>,
    }),
    valueSetter: (params: { oldValue: IPrebook; newValue: IPrebook; data: IRowData }) => {
        if (params.oldValue.prebook === params.newValue.prebook) {
            return false;
        }
        params.data.prebook = params.newValue.prebook;
        return true;
    },
    onCellValueChanged: ({ oldValue, newValue, data }: { oldValue: IPrebook; newValue: IPrebook; data: IRowData }) => {
        if (oldValue.prebook === newValue.prebook) {
            return;
        }
        const update = { ...mapLineItem(data), prebook: newValue.prebook };
        const action = data.isVirtual ? addLineItem(data.item.id, update) : updateLineItem(data.item.id, update);
        postToWindow(updateWorkingOrder(action));
    },
});

const getBulkUnitsColDef = (): ColDef => ({
    headerName: format("Bulk Units"),
    colId: BULK_UNITS_SIZE,
    cellClass: (row) => classnames(getCellClasses(row.node.rowPinned), "right-aligned"),
    width: 96,
    suppressMenu: true,
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    singleClickEdit: true,
    editable: false,
    suppressNavigable: true,
    headerClass: "right-aligned",
    equals: (a: IBulkUnits, b: IBulkUnits) => a.units + b.units === 0 || isEqualBy(["units", "sized"])(a, b), // If units are not set, we always show `-` in the input
    valueGetter: (row: { data: IRowData }): IBulkUnits => row.data?.item?.bulkUnits ?? getBulkUnits(),
    cellRendererSelector: (params: CastProp<ICellRendererParams, "value", IBulkUnits>) => {
        const { node, value, colDef } = params;
        const { units = 0, sized = 0 } = value || {};
        const editable = _.isFunction(colDef.editable)
            ? colDef.editable(params as EditableCallbackParams)
            : colDef.editable;
        return {
            component: node.rowPinned === "bottom" ? Components.DetailsGridTotalsRowRenderer : Components.InputRenderer,
            params: {
                value: units || "",
                input: {
                    dataTest: SHIPMENT_REVIEW.BULK_UNITS.UNITS,
                    classes: { warn: units > 0 && sized > units, strikethrough: sized > 0, notAvailable: !editable },
                },
                hint: {
                    value: units > 0 && sized > 0 ? format("Sized: |n|", { n: sized }) : "",
                    dataTest: SHIPMENT_REVIEW.BULK_UNITS.SIZED,
                    classes: sized > units && "inventory__warning",
                },
            } as InputRendererProps,
        };
    },
});

export const getSizingGridColDefs = (options: {
    uniqueSizes: ISize[];
    currency: IOrderCurrency;
    showWarehouse: boolean;
    showPrebook: boolean;
    showBulkUnits: boolean;
    fdm: Schema["fields"];
    customizations?: ICustomizationData;
    pricesPerSizeMap?: IDetailHeaderPrices;
}) => {
    const { uniqueSizes, currency, showWarehouse, showPrebook, showBulkUnits, fdm, customizations, pricesPerSizeMap } =
        options;
    const colDefs: ColDef[] = [];

    if (showWarehouse) {
        colDefs.push(getWarehouseColDef());
    }

    if (showPrebook) {
        colDefs.push(getPrebookColDef());
    }

    colDefs.push(
        buildShipmentColDef({
            width: SHIPMENT_COL_WIDTH,
            pinnedFooterRowRenderer: Components.DetailsGridTotalsRowRenderer,
            collapseCurrentYear: true,
        }),
    );

    if (showBulkUnits) {
        colDefs.push(getBulkUnitsColDef());
    }

    uniqueSizes.forEach((size) => {
        colDefs.push(
            buildSizeColDef(size, {
                pinnedFooterRowRenderer: Components.DetailsGridTotalsRowRenderer,
                headerParams: pricesPerSizeMap && { priceDetails: pricesPerSizeMap[size.name] },
            }),
        );
    });

    if (customizations) {
        colDefs.push({
            headerName: format("Customizations"),
            colId: "customizations",
            width: 150,
            cellRendererSelector: () => ({
                component: OtherViewComponents.CustomizationsRenderer,
            }),
        });
    }

    getSupportedFDMFields(fdm)?.forEach((field) => colDefs.push(buildFdmColDef(field)));

    colDefs.push(
        getTotalColDef({
            headerName: format("Total Qty"),
            colId: "totalQty",
            getValue: (totals) => _.toString(totals.quantity),
        }),
    );

    colDefs.push(
        getTotalColDef({
            headerName: format("Total Wholesale"),
            colId: "totalCost",
            width: 135,
            showStrikethroughedPrices: true,
            equals: _.isEqual,
            getValue: (totals) => [totals?.totalWholesalePrice, totals?.totalOriginalWholesalePrice],
            valueFormatter: ({ value }) =>
                _.castArray(value)
                    .map((v) => currencyValueFormatter(currency.wholesale.symbol, currency.wholesale.decimalPlaces, v))
                    .join(";"),
        }),
    );

    colDefs.push(getDetailsMenuColDef(uniqueSizes));

    return _.compact(colDefs);
};
